<template>
  <v-dialog v-model="dialog" max-width="920" scrollable>
    <v-card class="mau-detail">
      <div class="mau-detail__header">
        <div class="d-flex align-center" style="min-width: 0">
          <div class="mau-detail__icon">
            <v-icon color="white">mdi-clipboard-check-outline</v-icon>
          </div>
          <div style="min-width: 0">
            <div class="mau-detail__title">Подробная информация о загрузке</div>
            <div class="mau-detail__id-row">
              <span class="mau-detail__id" :title="massUploadId">{{ massUploadId }}</span>
              <v-btn
                v-if="massUploadId"
                icon
                x-small
                dark
                class="ml-1"
                @click="copyId"
              >
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div v-if="meta" class="mau-detail__meta">
        <div class="mau-detail__meta-item">
          <div class="mau-detail__meta-label">Файл</div>
          <div class="mau-detail__meta-value">
            <v-icon x-small color="primary" class="mr-1">{{ isLcd ? 'mdi-file-image-outline' : 'mdi-file-music-outline' }}</v-icon>
            <expandable-text
              :key="massUploadId"
              :text="meta.real_name || meta.file_name"
              :max-length="36"
              single-line-collapsed
            />
          </div>
        </div>
        <div class="mau-detail__meta-item">
          <div class="mau-detail__meta-label">Автор</div>
          <div class="mau-detail__meta-value">{{ meta.user_name || 'Система' }}</div>
        </div>
        <div class="mau-detail__meta-item">
          <div class="mau-detail__meta-label">Создано</div>
          <div class="mau-detail__meta-value">{{ formatDate(meta.task_creating_date) }}</div>
        </div>
        <div class="mau-detail__meta-item">
          <div class="mau-detail__meta-label">Расписание</div>
          <div class="mau-detail__meta-value">
            <template v-if="meta.start_date || meta.end_date">
              {{ formatDate(meta.start_date) }} → {{ formatDate(meta.end_date) }}
            </template>
            <template v-else>сразу</template>
          </div>
        </div>
      </div>

      <v-card-text class="mau-detail__body">
        <div class="mau-detail__toolbar">
          <div class="mau-detail__stats">
            <v-chip x-small label color="blue lighten-5" text-color="primary" class="mr-1">
              Всего: {{ stats.total }}
            </v-chip>
            <v-chip
              x-small
              label
              color="green lighten-4"
              text-color="green darken-3"
              class="mr-1"
            >
              <v-icon left x-small>mdi-check-circle</v-icon>
              Успешно: {{ stats.success_count }}
            </v-chip>
            <v-chip
              x-small
              label
              color="red lighten-4"
              text-color="red darken-3"
            >
              <v-icon left x-small>mdi-alert-circle</v-icon>
              Ошибки: {{ stats.fail_count }}
            </v-chip>
          </div>
          <div class="mau-detail__downloads">
            <v-btn
              color="primary"
              class="text-none mr-2 mb-1"
              rounded
              outlined
              small
              :loading="downloadingAudio"
              :disabled="isLcd && !hasDownloadableFile"
              @click="downloadAudio"
            >
              <v-icon left small>{{ isLcd ? 'mdi-file-image-outline' : 'mdi-file-music-outline' }}</v-icon>
              {{ isLcd ? 'Изображение' : 'Аудио' }}
            </v-btn>
            <v-btn
              color="primary"
              class="text-none mb-1"
              rounded
              small
              :loading="exporting"
              @click="downloadResult"
            >
              <v-icon left small>mdi-file-delimited-outline</v-icon>
              CSV
            </v-btn>
          </div>
        </div>

        <div class="mau-detail__filter mb-3">
          <v-btn-toggle
            v-model="statusFilter"
            mandatory
            dense
            color="primary"
            class="mau-detail__filter-toggle"
          >
            <v-btn small value="all" class="text-none">
              Все
            </v-btn>
            <v-btn small value="success" class="text-none">
              Успешные
            </v-btn>
            <v-btn small value="fail" class="text-none">
              Ошибки
            </v-btn>
          </v-btn-toggle>
        </div>

        <v-data-table
          class="table mau-detail__table"
          dense
          :headers="headers"
          :items="rows"
          :options.sync="options"
          :server-items-length="total"
          :loading="loading"
          item-key="id"
          disable-sort
          :footer-props="{
            'items-per-page-options': [10, 20, 50],
            showFirstLastPage: true,
          }"
        >
          <template v-slot:item.doorphone_id="{ item }">
            <span class="mau-detail__dp-id">{{ item.doorphone_id }}</span>
          </template>
          <template v-slot:item.address="{ item }">
            <router-link
              class="mau-detail__link"
              :to="`/doorphones/${item.doorphone_id}`"
              @click.native="close"
            >
              {{ item.address }}
            </router-link>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              x-small
              label
              :color="item.status ? 'green lighten-4' : 'red lighten-4'"
              :text-color="item.status ? 'green darken-3' : 'red darken-3'"
            >
              <v-icon left x-small>
                {{ item.status ? 'mdi-check-circle' : 'mdi-alert-circle' }}
              </v-icon>
              {{ item.status ? 'Успешно' : 'Ошибка' }}
            </v-chip>
          </template>
          <template v-slot:no-data>
            <div class="mau-detail__empty">
              <v-icon color="grey lighten-1" class="mb-2">mdi-timer-sand</v-icon>
              <div>{{ emptyMessage }}</div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import ExpandableText from '@/components/ExpandableText';

export const MASS_AUDIO_OPEN_RESULT_EVENT = 'mass-audio-open-result';

export default {
  name: 'MassAudioUploadResultDialog',
  components: {
    ExpandableText,
  },
  data () {
    return {
      dialog: false,
      massUploadId: null,
      mediaTypeHint: 'audio',
      rows: [],
      total: 0,
      metaCache: null,
      stats: {
        total: 0,
        success_count: 0,
        fail_count: 0,
      },
      statusFilter: 'all',
      loading: false,
      exporting: false,
      downloadingAudio: false,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      headers: [
        { text: 'ID', value: 'doorphone_id', width: 90 },
        { text: 'Адрес', value: 'address' },
        { text: 'Статус', value: 'status', width: 140 },
      ],
    };
  },
  computed: {
    meta () {
      return this.rows?.[0] || this.metaCache || null;
    },
    isLcd () {
      const fromRow = this.meta?.media_type;
      if (fromRow) return fromRow === 'lcd';
      return this.mediaTypeHint === 'lcd';
    },
    hasDownloadableFile () {
      const path = this.meta?.real_name;
      return !!(path && String(path).trim());
    },
    emptyMessage () {
      if (this.statusFilter === 'success') {
        return 'Нет успешных загрузок.';
      }
      if (this.statusFilter === 'fail') {
        return 'Нет ошибок загрузки.';
      }
      return 'Нет строк результата — задача ещё выполняется или пуста.';
    },
  },
  watch: {
    options: {
      deep: true,
      handler () {
        if (!this.dialog || !this.massUploadId) return;
        this.loadDetail(this.massUploadId);
      },
    },
    statusFilter () {
      if (!this.dialog || !this.massUploadId || this.suppressStatusReload) return;
      if (this.options.page !== 1) {
        this.options.page = 1;
        return;
      }
      this.loadDetail(this.massUploadId);
    },
  },
  mounted () {
    this._onOpen = (event) => {
      const massUploadId = event?.detail?.massUploadId;
      if (massUploadId) {
        this.mediaTypeHint = event?.detail?.mediaType || 'audio';
        this.open(massUploadId);
      }
    };
    window.addEventListener(MASS_AUDIO_OPEN_RESULT_EVENT, this._onOpen);
  },
  beforeDestroy () {
    if (this._onOpen) {
      window.removeEventListener(MASS_AUDIO_OPEN_RESULT_EVENT, this._onOpen);
    }
  },
  methods: {
    ...mapActions({
      detailInfoAudioUpload: 'Doorphone/detailInfoAudioUpload',
      exportResult: 'MassAudioUploadJobs/exportResult',
      downloadAudioFileAction: 'MassAudioUploadJobs/downloadAudioFile',
    }),
    open (massUploadId) {
      this.suppressStatusReload = true;
      this.massUploadId = massUploadId;
      this.dialog = true;
      this.rows = [];
      this.total = 0;
      this.metaCache = null;
      this.stats = { total: 0, success_count: 0, fail_count: 0 };
      this.statusFilter = 'all';
      this.suppressStatusReload = false;
      if (this.options.page === 1) {
        this.loadDetail(massUploadId);
      } else {
        this.options.page = 1;
      }
    },
    close () {
      this.dialog = false;
    },
    formatDate (value) {
      if (!value) return '—';
      const m = moment(value);
      return m.isValid() ? m.format('DD.MM.YYYY HH:mm') : value;
    },
    async copyId () {
      if (!this.massUploadId) return;
      try {
        await navigator.clipboard.writeText(String(this.massUploadId));
        if (typeof this.$flash_success === 'function') {
          this.$flash_success('Код скопирован');
        } else {
          this.$flash_warning('Код скопирован');
        }
      } catch (e) {
        this.$flash_warning('Не удалось скопировать');
      }
    },
    statusQueryValue () {
      if (this.statusFilter === 'success') return 1;
      if (this.statusFilter === 'fail') return 0;
      return undefined;
    },
    loadDetail (massUploadId) {
      this.loading = true;
      const options = {
        limit: this.options.itemsPerPage,
        page: this.options.page,
      };
      const status = this.statusQueryValue();
      if (status !== undefined) {
        options.status = status;
      }
      this.detailInfoAudioUpload({
        massUploadId,
        options,
      })
        .then((data) => {
          this.rows = data.data || [];
          this.total = data.meta?.total || 0;
          if (this.rows[0]) {
            this.metaCache = this.rows[0];
          }
          this.stats = {
            total: data.stats?.total ?? this.total,
            success_count: data.stats?.success_count ?? 0,
            fail_count: data.stats?.fail_count ?? 0,
          };
        })
        .catch(() => {
          this.rows = [];
          this.total = 0;
          this.metaCache = null;
          this.stats = { total: 0, success_count: 0, fail_count: 0 };
          this.$flash_warning('Ошибка при загрузке подробной информации');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    downloadResult () {
      if (!this.massUploadId) return;
      this.exporting = true;
      this.exportResult(this.massUploadId)
        .catch(() => this.$flash_warning('Не удалось скачать CSV'))
        .finally(() => {
          this.exporting = false;
        });
    },
    downloadAudio () {
      if (!this.massUploadId) return;
      this.downloadingAudio = true;
      this.downloadAudioFileAction(this.massUploadId)
        .catch(() => this.$flash_warning(this.isLcd ? 'Не удалось скачать изображение' : 'Не удалось скачать аудиофайл'))
        .finally(() => {
          this.downloadingAudio = false;
        });
    },
  },
};
</script>

<style scoped>
.mau-detail {
  overflow: hidden;
}
.mau-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(135deg, #0d4092, #1e88e5);
  color: #fff;
}
.mau-detail__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}
.mau-detail__title {
  font-weight: 700;
  font-size: 1.05rem;
}
.mau-detail__id-row {
  display: flex;
  align-items: center;
  margin-top: 2px;
  min-width: 0;
}
.mau-detail__id {
  font-size: 0.75rem;
  opacity: 0.9;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mau-detail__meta {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px 16px;
  padding: 14px 18px;
  background: #f5f9fc;
  border-bottom: 1px solid #e3eef6;
}
.mau-detail__meta-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #90a4ae;
  margin-bottom: 2px;
}
.mau-detail__meta-value {
  font-size: 0.85rem;
  color: #37474f;
  display: flex;
  align-items: flex-start;
  min-width: 0;
}
.mau-detail__meta-value .v-icon {
  margin-top: 2px;
  flex-shrink: 0;
}
.mau-detail__meta-value ::v-deep .expandable-text {
  min-width: 0;
  flex: 1;
}
.mau-detail__body {
  padding: 14px 18px 8px !important;
}
.mau-detail__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.mau-detail__downloads {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.mau-detail__filter-toggle {
  background: #fff;
  border: 1px solid #e3eef6;
  border-radius: 8px;
  overflow: hidden;
}
.mau-detail__dp-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
}
.mau-detail__link {
  color: #0d4092;
  text-decoration: none;
}
.mau-detail__link:hover {
  text-decoration: underline;
}
.mau-detail__empty {
  text-align: center;
  padding: 28px 12px;
  color: #90a4ae;
  font-size: 0.9rem;
}
@media (max-width: 800px) {
  .mau-detail__meta {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
